import { groupDigits } from './payoutSummary';
import type {
  ImportCheckResult,
  ImportCheckedRow,
  ImportCurrencyTotal,
  ImportRowIssue,
} from '@/api/imports';

export interface CellIssue {
  message: string;
  severity: 'error' | 'notice';
}

export function cellKey(rowNumber: number, column: string): string {
  return `${rowNumber}::${column}`;
}

/**
 * 把 issue 按 (行号, 列) 归位，表格渲染时一格一格查。
 * 🔴 同一格既有 error 又有 notice 时显示 error —— 「这一列用不上」不该盖住「这一格填错了」。
 */
export function issuesByCell(rows: readonly ImportCheckedRow[]): Map<string, CellIssue> {
  const map = new Map<string, CellIssue>();
  for (const row of rows) {
    for (const i of row.issues) {
      if (i.column === null) continue;
      const key = cellKey(i.rowNumber, i.column);
      const prev = map.get(key);
      if (prev?.severity === 'error') continue;
      map.set(key, { message: i.message, severity: i.severity });
    }
  }
  return map;
}

/** 整行级的问题（组合不支持、拿不到 schema 这类），不属于任何一格。 */
export function rowLevelIssues(row: ImportCheckedRow): ImportRowIssue[] {
  return row.issues.filter((i) => i.column === null);
}

export function rowSeverity(row: ImportCheckedRow): 'error' | 'notice' | 'ok' {
  if (row.issues.some((i) => i.severity === 'error')) return 'error';
  if (row.issues.length > 0) return 'notice';
  return 'ok';
}

/**
 * 「立即创建申请」能不能点。
 *
 * 🔴 `done` 之后永远是 false。重复提交会造出一批**在打款申请页看不出是重复**的
 * PENDING 申请，而财务会把它们全打出去（见后端 payout_import_batches 的说明）。
 * 服务端的幂等 token 是边界，这里只是不给入口。
 */
export function canSubmit(
  result: ImportCheckResult | null,
  submitting: boolean,
  done: boolean,
): boolean {
  if (!result || submitting || done) return false;
  if (result.errorCount > 0) return false;
  return result.rows.some((r) => r.draft !== null);
}

/** 🔴 按币种分行，绝不跨币种相加（同 PRODUCT.md §4.7）。千分位在字符串上加。 */
export function totalsSummary(totals: readonly ImportCurrencyTotal[]): string {
  if (totals.length === 0) return '—';
  return totals.map((t) => `${groupDigits(t.amount)} ${t.currency}（${t.count} 笔）`).join(' · ');
}
